/**
 * Edit role — change a proposed or planned role in every dimension the proposal form sets.
 *
 * Opened from the pencil on a planned or proposed row of the Org Planning Overview. Loads the
 * record fresh (`GET /roles/:source/:id`) rather than reading the timeline payload, because the
 * timeline deliberately carries neither `Unit` nor `Track and Level`.
 *
 * Three things the form says out loud, because each is decided by the proxy and would otherwise
 * surprise someone (the rules are in `proxy-server/lib/plannedRoleEdits.js`):
 *
 *   - **Which date moves.** On a hiring-plan row the date field is the ADJUSTED planned start date,
 *     with the committed planned date shown read-only beside it. Clearing it falls back to the
 *     planned one. On a proposal it is the planned date itself.
 *   - **Over 10% goes to the approvers**, for everyone (David, Sep 25 2026). The proxy answers the
 *     first save with what WOULD happen and the form asks before the confirming second one: a
 *     proposal is saved and its approvals reset; a hiring-plan row is NOT changed — the edit is
 *     filed as a change request and written only once the CPO, CEO and CFO have approved it.
 *   - **An open change request locks the row** until it is decided or withdrawn.
 *
 * Native `<select>` / `<input>` for the same documented reason as `ProposeRoleModal`.
 */
import { useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { AlertTriangle } from 'lucide-react';
import { Modal, ModalContent, ModalHeader, ModalTitle } from '@finn-auto/design-system/atoms/modal';
import { Button } from '@finn-auto/design-system/atoms/button';
import { fetchProposalFieldOptions, type OrgNode } from '../../services/internalPlaygroundApi';
import {
  ApprovalRequiredError,
  RoleEditRefusedError,
  RoleEditValidationError,
  fetchEditableRole,
  saveRoleEdit,
  type EditableRole,
  type RoleEditResult,
  type RoleEditValues,
  type RoleSource,
} from '../../services/plannedRoleEditsApi';
import { CompensationSummary } from '../../components/CompensationSummary';
import { formatEuro } from '../../types/compensation';
import { useRoleCompensation } from './useRoleCompensation';
import { FIELD_CLASS, hiringManagerOptions, unitOptionsForDepartment } from './proposeRole';
import { ChoiceToggle, Field } from './proposeRoleUi';
import { changedValues, effectiveFormStart, withCurrent } from './editRole';
import { formatDay } from './backfillForm';
import { useViewerScope } from './viewerScope';

const TIMELINE_QUERY_KEY = ['internal-playground-hiring-timeline'];
const APPROVALS_QUERY_KEY = ['role-approvals'];

export interface EditRoleTarget {
  source: RoleSource;
  recordId: string;
  label: string;
}

export function EditRoleModal({
  target,
  root,
  onClose,
  onSaved,
}: {
  target: EditRoleTarget;
  root: OrgNode;
  onClose: () => void;
  /** Called with the result before the modal closes, so the PAGE can warn about Slack. */
  onSaved?: (result: RoleEditResult) => void;
}) {
  const roleQuery = useQuery({
    queryKey: ['planned-role', target.source, target.recordId],
    queryFn: () => fetchEditableRole(target.source, target.recordId),
    retry: false,
    // Always fresh: this is the "before" the save is judged against.
    staleTime: 0,
    gcTime: 0,
  });

  return (
    <Modal open onOpenChange={(open: boolean) => !open && onClose()}>
      <ModalContent className="!h-max !max-h-[calc(100dvh-2rem)] !w-[min(100vw-2rem,40rem)] gap-4 overflow-auto rounded p-6">
        <ModalHeader>
          <ModalTitle className="!text-xl !font-semibold">Edit role: {target.label}</ModalTitle>
        </ModalHeader>
        {roleQuery.isPending && <p className="text-sm text-zinc-500">Loading the role…</p>}
        {roleQuery.error && (
          <>
            <p className="text-sm text-red-600">
              {roleQuery.error instanceof Error ? roleQuery.error.message : 'Could not load the role.'}
            </p>
            <div className="flex justify-end">
              <Button type="button" variant="outline" size="sm" onClick={onClose}>
                Close
              </Button>
            </div>
          </>
        )}
        {roleQuery.data && (
          <EditRoleForm
            role={roleQuery.data.role}
            root={root}
            onClose={onClose}
            onSaved={onSaved}
          />
        )}
      </ModalContent>
    </Modal>
  );
}

function EditRoleForm({
  role,
  root,
  onClose,
  onSaved,
}: {
  role: EditableRole;
  root: OrgNode;
  onClose: () => void;
  onSaved?: (result: RoleEditResult) => void;
}) {
  const queryClient = useQueryClient();
  const [form, setForm] = useState<RoleEditValues>(role.values);
  const [fieldErrors, setFieldErrors] = useState<Partial<Record<keyof RoleEditValues, string>>>({});
  /** Set by the proxy's first answer when the edit needs approval — the confirm step. */
  const [pendingReapproval, setPendingReapproval] = useState<ApprovalRequiredError | null>(null);

  const isProposal = role.source === 'proposal';
  /** A change already awaiting approval: the row takes no other edit until it is settled. */
  const locked = Boolean(role.openChangeRequest);
  const canEdit = !locked;
  const changes = useMemo(() => changedValues(role.values, form), [role.values, form]);
  const dirty = Object.keys(changes).length > 0;

  const optionsQuery = useQuery({
    queryKey: ['internal-playground-proposal-options'],
    queryFn: fetchProposalFieldOptions,
    staleTime: 10 * 60 * 1000,
    retry: false,
  });
  const options = optionsQuery.data;

  const scope = useViewerScope();
  const managers = useMemo(() => {
    // A proposer may only move the role to someone in their own line (the proxy refuses others).
    const list = hiringManagerOptions(root).filter((option) => scope.inLine(option.employeeId));
    // The current manager may have left, or sit outside the tree — keep them pickable, or the
    // select would show someone else and a save would appear to change the manager.
    const current = role.values.hiringManagerEmployeeId;
    if (current && !list.some((m) => m.employeeId === current)) {
      return [{ employeeId: current, name: role.hiringManagerName || current }, ...list];
    }
    return list;
  }, [root, scope, role.values.hiringManagerEmployeeId, role.hiringManagerName]);

  const pricedRows = useMemo(
    () => [
      {
        trackAndLevel: form.trackAndLevel,
        plannedStartDate: effectiveFormStart(role.source, form.startDate, role.plannedStartDate),
      },
    ],
    [form.trackAndLevel, form.startDate, role.source, role.plannedStartDate]
  );
  const compensation = useRoleCompensation(form.role, form.peopleTeam, pricedRows);

  const mutation = useMutation({
    mutationFn: ({ confirm }: { confirm: boolean }) =>
      saveRoleEdit(role.source, role.id, changes, { confirmApproval: confirm }),
    onSuccess: (result) => {
      void queryClient.invalidateQueries({ queryKey: TIMELINE_QUERY_KEY });
      if (isProposal || result.changeRequested) void queryClient.invalidateQueries({ queryKey: APPROVALS_QUERY_KEY });
      onSaved?.(result);
      onClose();
    },
    onError: (error) => {
      if (error instanceof ApprovalRequiredError) {
        setPendingReapproval(error);
        return;
      }
      setFieldErrors(error instanceof RoleEditValidationError ? error.details : {});
    },
  });

  const set = (key: keyof RoleEditValues) => (value: string) => {
    setForm((prev) => ({ ...prev, [key]: value }));
    setFieldErrors((prev) => (prev[key] ? { ...prev, [key]: undefined } : prev));
    // Anything typed after the question was asked makes the question stale.
    setPendingReapproval(null);
  };

  const save = (confirm: boolean) => {
    // `loading` on the DS Button only blocks the mouse; Enter still submits.
    if (mutation.isPending || !dirty) return;
    mutation.mutate({ confirm });
  };

  const unitOptions = useMemo(
    () => withCurrent(unitOptionsForDepartment(options?.unit ?? [], form.peopleTeam), form.unit),
    [options, form.peopleTeam, form.unit]
  );

  const selectField = (key: 'role' | 'peopleTeam' | 'unit' | 'trackAndLevel') => {
    const choices = key === 'unit' ? unitOptions : withCurrent(options?.[key] ?? [], role.values[key]);
    const onChange = (value: string) => {
      if (key === 'peopleTeam') {
        const nextUnits = unitOptionsForDepartment(options?.unit ?? [], value);
        set('peopleTeam')(value);
        setForm((prev) => ({ ...prev, unit: prev.unit && nextUnits.includes(prev.unit) ? prev.unit : '' }));
        return;
      }
      set(key)(value);
    };
    return (
      <select
        id={`edit-${key}`}
        className={FIELD_CLASS}
        value={form[key] ?? ''}
        onChange={(e) => onChange(e.target.value)}
        disabled={!options || !canEdit}
      >
        <option value="">{options ? '—' : optionsQuery.isPending ? 'Loading…' : 'Unavailable'}</option>
        {choices.map((choice) => (
          <option key={choice} value={choice}>
            {choice}
          </option>
        ))}
      </select>
    );
  };

  const toggleField = (key: 'reasonForHiring' | 'costCategory') => (
    <ChoiceToggle
      labelledBy={`edit-${key}-label`}
      options={withCurrent(options?.[key] ?? [], role.values[key])}
      value={form[key] ?? ''}
      onChange={set(key)}
      // A row linked to a leaver is a backfill; the proxy refuses changing that as well.
      disabled={!options || !canEdit || (role.backfillLinked && key === 'reasonForHiring')}
    />
  );

  const refusal = mutation.error instanceof RoleEditRefusedError ? mutation.error : null;
  const otherError =
    mutation.error && !(mutation.error instanceof RoleEditValidationError) && !(mutation.error instanceof ApprovalRequiredError)
      ? mutation.error
      : null;

  return (
    <form
      className="space-y-4"
      onSubmit={(e) => {
        e.preventDefault();
        save(false);
      }}
    >
      <p className="text-sm text-zinc-500">
        {isProposal
          ? `Changes the proposal (${role.status}). Changes within 10% of the cost are saved straight away; a bigger increase sends it back to the CPO, CEO and CFO.`
          : `Changes this ${role.status} role in the Hiring Database. Changes within 10% of the cost are saved straight away; a bigger increase goes to the CPO, CEO and CFO first and is only applied once all three approve.`}
      </p>

      {role.openChangeRequest && (
        <div className="flex items-start gap-2 rounded border border-amber-200 bg-amber-50 p-3 text-xs text-amber-900">
          <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />
          <div>
            <p>
              A change requested by {role.openChangeRequest.requestedBy} is waiting for approval, so this role cannot be
              edited until it is decided or withdrawn on the Role Approvals page.
            </p>
            {role.openChangeRequest.descriptions.length > 0 && (
              <ul className="mt-1 list-disc pl-4">
                {role.openChangeRequest.descriptions.map((line) => (
                  <li key={line}>{line}</li>
                ))}
              </ul>
            )}
          </div>
        </div>
      )}

      <Field label="Title" htmlFor="edit-title" required error={fieldErrors.title}>
        <input
          id="edit-title"
          className={FIELD_CLASS}
          value={form.title}
          disabled={!canEdit}
          onChange={(e) => set('title')(e.target.value)}
        />
      </Field>

      <div className="grid grid-cols-2 gap-3">
        <Field label="Role" htmlFor="edit-role" required error={fieldErrors.role}>
          {selectField('role')}
        </Field>
        <Field label="Department (Reporting Line)" htmlFor="edit-peopleTeam" required error={fieldErrors.peopleTeam}>
          {selectField('peopleTeam')}
        </Field>
        <Field
          label="Hiring manager"
          htmlFor="edit-hiringManagerEmployeeId"
          required
          error={fieldErrors.hiringManagerEmployeeId}
        >
          <select
            id="edit-hiringManagerEmployeeId"
            className={FIELD_CLASS}
            value={form.hiringManagerEmployeeId}
            disabled={!canEdit}
            onChange={(e) => set('hiringManagerEmployeeId')(e.target.value)}
          >
            <option value="">—</option>
            {managers.map((m) => (
              <option key={m.employeeId} value={m.employeeId}>
                {m.name}
              </option>
            ))}
          </select>
        </Field>
        <Field
          label={isProposal ? 'Planned start date' : 'Adjusted planned start date'}
          htmlFor="edit-startDate"
          required={isProposal}
          error={fieldErrors.startDate}
        >
          <input
            id="edit-startDate"
            type="date"
            className={FIELD_CLASS}
            value={form.startDate}
            disabled={!canEdit}
            onChange={(e) => set('startDate')(e.target.value)}
          />
          {!isProposal && (
            <p className="mt-1 text-xs text-zinc-500">
              Planned: {role.plannedStartDate ? formatDay(role.plannedStartDate) : '—'} (committed, not changed here)
              {form.startDate ? '' : '. Empty means the planned date applies.'}
            </p>
          )}
        </Field>
        <Field label="Unit" htmlFor="edit-unit" error={fieldErrors.unit}>
          {selectField('unit')}
        </Field>
        <Field label="Track and level" htmlFor="edit-trackAndLevel" error={fieldErrors.trackAndLevel}>
          {selectField('trackAndLevel')}
        </Field>
        <Field label="Reason for hiring" labelId="edit-reasonForHiring-label" error={fieldErrors.reasonForHiring}>
          {toggleField('reasonForHiring')}
        </Field>
        <Field label="Cost category" labelId="edit-costCategory-label" error={fieldErrors.costCategory}>
          {toggleField('costCategory')}
        </Field>
      </div>

      <div className="space-y-1">
        <CompensationSummary compensation={compensation.first} busy={compensation.isPending} />
        {role.compensation && !role.compensation.unavailable && (
          <p className="text-xs text-zinc-500">
            Before this edit: {formatEuro(role.compensation.costUntilYearEnd)} until 31 Dec {role.compensation.year}.
          </p>
        )}
      </div>

      {isProposal && (
        <Field label="Why this role?" htmlFor="edit-proposalRational" required error={fieldErrors.proposalRational}>
          <textarea
            id="edit-proposalRational"
            rows={4}
            className={FIELD_CLASS}
            value={form.proposalRational ?? ''}
            disabled={!canEdit}
            onChange={(e) => set('proposalRational')(e.target.value)}
          />
        </Field>
      )}

      {pendingReapproval && (
        <div role="alert" className="space-y-2 rounded border border-amber-200 bg-amber-50 p-3 text-sm text-amber-900">
          <p className="font-medium">This change needs approval from the CPO, CEO and CFO.</p>
          <p className="text-xs">
            {pendingReapproval.reason === 'uncostable'
              ? 'It could raise the cost, and the role cannot be costed against its salary band before and after it.'
              : `It raises the cost by more than 10%${costDelta(pendingReapproval)}.`}{' '}
            {pendingReapproval.kind === 'change_request'
              ? 'Nothing changes in the Hiring Database yet: the change is sent to the channel and applied once all three approve.'
              : `The proposal is saved and goes back for approval${
                  pendingReapproval.cleared > 0
                    ? ` — ${pendingReapproval.cleared === 1 ? 'the recorded decision is' : `${pendingReapproval.cleared} recorded decisions are`} cleared`
                    : ''
                }, and the approvers are asked in the request's Slack thread.`}
          </p>
        </div>
      )}

      {refusal && (
        <p role="alert" className="text-xs text-red-600">
          {refusal.message}
        </p>
      )}
      {otherError && !refusal && (
        <p className="text-xs text-red-600">
          {otherError instanceof Error ? otherError.message : 'Could not save the change.'}
        </p>
      )}

      <div className="flex justify-end gap-2 pt-1">
        <Button type="button" variant="outline" size="sm" onClick={onClose}>
          Cancel
        </Button>
        {pendingReapproval ? (
          <Button
            type="button"
            size="sm"
            loading={mutation.isPending}
            disabled={mutation.isPending}
            onClick={() => save(true)}
          >
            {pendingReapproval.kind === 'change_request' ? 'Send for approval' : 'Save and send for re-approval'}
          </Button>
        ) : (
          <Button type="submit" size="sm" loading={mutation.isPending} disabled={!canEdit || !dirty || mutation.isPending}>
            Save changes
          </Button>
        )}
      </div>
    </form>
  );
}

/** ` (€10.000 → €14.000 until year end)` when both sides are priced. */
function costDelta(error: ApprovalRequiredError): string {
  const before = error.cost?.before?.costUntilYearEnd;
  const after = error.cost?.after?.costUntilYearEnd;
  if (!Number.isFinite(before) || !Number.isFinite(after)) return '';
  return ` (${formatEuro(before)} → ${formatEuro(after)} until year end)`;
}
